import { z } from "zod";
import type { SlideType } from "./deck";

/**
 * Images on slides.
 *
 * Only the types whose stage has room to spare take one: a question with answer tiles
 * (the image fills the band between question and tiles) and a content slide (beside
 * its words). The rest — word clouds, scales, open answers, Q&A — fill the stage with
 * the room's responses, and an image there would crowd out the thing being shown.
 */
export const MEDIA_TYPES = ["mcq", "true_false", "poll", "content"] as const satisfies readonly SlideType[];

export const takesMedia = (type: SlideType) => (MEDIA_TYPES as readonly string[]).includes(type);

/** What may be uploaded, and the extension each is stored under. No SVG: it can carry script. */
export const MEDIA_CONTENT_TYPES = {
  "image/webp": "webp",
  "image/jpeg": "jpg",
  "image/png": "png",
} as const;

export type MediaContentType = keyof typeof MEDIA_CONTENT_TYPES;

export const isMediaContentType = (type: string): type is MediaContentType => type in MEDIA_CONTENT_TYPES;

/**
 * An uploaded image's address, relative to the web app, which serves it:
 * `/media/<org id>/<random uuid>.<ext>` — the org id as Better Auth issues it. The random id is what keeps it private — it is
 * never listed, and cannot be guessed.
 */
export const MEDIA_PATH = /^\/media\/([A-Za-z0-9_-]{1,64})\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(?:webp|jpg|png))$/;

export const mediaUrlSchema = z.string().regex(MEDIA_PATH, "not an uploaded image");

/** The org an image belongs to and its storage key, or null for anything that is not one. */
export function parseMediaPath(url: string): { orgId: string; file: string; key: string } | null {
  const m = MEDIA_PATH.exec(url);
  return m ? { orgId: m[1]!, file: m[2]!, key: `${m[1]}/${m[2]}` } : null;
}

export const uploadedMediaSchema = z.object({ url: mediaUrlSchema }).strict();
export type UploadedMedia = z.infer<typeof uploadedMediaSchema>;

/**
 * What the bytes actually are, from their first few, or null for anything that is not
 * a WebP, JPEG or PNG — the declared content type is the uploader's claim, this is the
 * file's own.
 */
export function sniffImage(bytes: Uint8Array): MediaContentType | null {
  const at = (i: number, ...values: number[]) => values.every((v, j) => bytes[i + j] === v);
  if (at(0, 0x52, 0x49, 0x46, 0x46) && at(8, 0x57, 0x45, 0x42, 0x50)) return "image/webp";
  if (at(0, 0xff, 0xd8, 0xff)) return "image/jpeg";
  if (at(0, 0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return "image/png";
  return null;
}
